import * as React from "react";

import { cn } from "@/lib/utils";

interface InputProps extends React.ComponentProps<"input"> {
  prefixIcon?: React.ReactNode;
  inlineLabel?: string;
  hasError?: boolean;
}

function Input({ className, type, prefixIcon, inlineLabel, style, hasError = false, ...props }: InputProps) {
  // Calculate padding based on label width (approximate: ~8px per character + 16px spacing)
  const labelWidth = inlineLabel ? `${inlineLabel.length * 8 + 48}px` : '0px';
  
  if (prefixIcon) {
    return (
      <div className="relative">
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none">
          {prefixIcon}
        </div>
        <input
          type={type}
          data-slot="input"
          className={cn(
            "file:text-foreground placeholder:text-muted-foreground selection:bg-action-solid selection:text-on-accent h-12 min-w-0 rounded-full bg-input-bg border border-control-border pl-11 pr-6 py-3 text-base font-normal transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            "focus:outline focus:outline-2 focus:outline-focus-ring focus:outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2",
            "aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
            "shadow-lg",
            className
          )}
          {...props}
        />
      </div>
    );
  }

  if (inlineLabel) {
    // Extract margin and width classes from className to apply to wrapper
    const marginClasses = className?.match(/\b(m[xy]?|mt|mb|ml|mr|mx|my)-\d+\b/g) || [];
    const widthClasses = className?.match(/\b(w-|min-w-|max-w-)[^\s]*\b/g) || [];
    const wrapperClasses = [...marginClasses, ...widthClasses].join(' ');
    const inputClassName = className?.replace(/\b(m[xy]?|mt|mb|ml|mr|mx|my)-\d+\b|\b(w-|min-w-|max-w-)[^\s]*\b/g, '').trim() || '';
    
    return (
      <div className={cn("relative", wrapperClasses)}>
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none text-sm font-normal">
          {inlineLabel}
        </div>
        <input
          type={type}
          data-slot="input"
          className={cn(
            "file:text-foreground placeholder:text-muted-foreground selection:bg-action-solid selection:text-on-accent h-12 w-full min-w-0 rounded-full bg-input-bg border border-control-border pr-6 py-3 text-base font-normal transition-[color,box-shadow,outline] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            hasError 
              ? "outline outline-1 outline-ring-error-subtle outline-offset-0 focus:outline-2 focus:outline-ring-error focus-visible:outline-2 focus-visible:outline-ring-error"
              : "focus:outline focus:outline-2 focus:outline-focus-ring focus:outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2",
            "aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
            "shadow-lg",
            inputClassName || undefined
          )}
          style={{ paddingLeft: labelWidth, ...style }}
          {...props}
        />
      </div>
    );
  }

  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        "file:text-foreground placeholder:text-muted-foreground selection:bg-action-solid selection:text-on-accent h-12 min-w-0 rounded-full bg-input-bg border border-control-border px-5 py-3 mb-3 text-base font-normal transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "focus:outline focus:outline-2 focus:outline-focus-ring focus:outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2",
        "aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
        "shadow-lg",
        className
      )}
      {...props}
    />
  );
}

export { Input };

